import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './Header.css';
import { 
  BnbIcon, 
  EthereumIcon, 
  TestnetFlaskIcon, 
  HucIcon,
  ArbitrumIcon,
  PolygonIcon,
  WalletIcon, 
  CopyIcon, 
  NavOverviewIcon, 
  NavSwapIcon, 
  NavLiquidityIcon, 
  NavBridgeIcon, 
  NavFaucetIcon,
  CheckIcon,
  WarningIcon,
  ErrorIcon,
  ChevronDownIcon,
  CloseIcon
} from '../common/Icons';
import Toast from '../common/Toast';

function Header({ 
  account, 
  connectWallet, 
  disconnectWallet, 
  networkConfig, 
  isCorrectNetwork, 
  chainId, 
  selectedNetwork, 
  onNetworkChange,
  currentTab,
  onTabChange,
  showApp,
  onShowAppChange
}) {
  const { t, language, setLanguage, languages } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [toast, setToast] = useState(null);

  const currentLanguageObj = languages.find(l => l.code === language) || languages[0];

  // Professional scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.network-control')) {
        setShowNetworkDropdown(false);
      }
      if (!e.target.closest('.language-control')) {
        setShowLanguageDropdown(false);
      }
      if (!e.target.closest('.wallet-control')) {
        setShowAccountDropdown(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowNetworkDropdown(false);
        setShowLanguageDropdown(false);
        setShowAccountDropdown(false);
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Professional address formatting
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Enhanced network status
  const getNetworkStatus = () => {
    if (!account) {
      return {
        name: t('header.notConnected'),
        status: 'disconnected',
        color: '#9ca3af',
        icon: <WarningIcon size={14} color="#f59e0b" />
      };
    }

    if (!isCorrectNetwork) {
      return {
        name: t('header.wrongNetwork'),
        status: 'wrong',
        color: '#ef4444',
        icon: <ErrorIcon size={14} color="#ef4444" />
      };
    }

    return {
      name: networkConfig?.chainName || 'Unknown',
      status: 'connected',
      color: '#10b981',
      icon: <CheckIcon size={14} color="#10b981" />
    };
  };

  const networkStatus = getNetworkStatus();

  // Professional navigation items with real SVG icons
  const navigationItems = [
    {
      key: 'home',
      label: t('header.overview'),
      icon: <NavOverviewIcon size={16} />,
      isActive: !showApp
    },
    {
      key: 'swap',
      label: t('header.swap'),
      icon: <NavSwapIcon size={16} />,
      isActive: showApp && currentTab === 'swap'
    },
    {
      key: 'liquidity',
      label: t('header.liquidity'),
      icon: <NavLiquidityIcon size={16} />,
      isActive: showApp && currentTab === 'liquidity'
    },
    {
      key: 'crosschain',
      label: t('header.bridge'),
      icon: <NavBridgeIcon size={16} />,
      isActive: showApp && currentTab === 'crosschain'
    },
    {
      key: 'faucet',
      label: t('header.faucet'),
      icon: <NavFaucetIcon size={16} />,
      isActive: showApp && currentTab === 'faucet'
    }
  ];

  // Enhanced network options with real chain SVG icons
  const networkOptions = [
    {
      key: 'huanchain_mainnet',
      name: 'HuanChain L2',
      icon: <HucIcon size={20} />,
      description: 'HuanChain L2 Network',
      chainId: 8888,
      status: 'active'
    },
    {
      key: 'bsc_mainnet',
      name: 'BNB Chain',
      icon: <BnbIcon size={20} />,
      description: 'BNB Chain Mainnet',
      chainId: 56,
      status: 'active'
    },
    {
      key: 'bsc_testnet',
      name: 'BSC Testnet',
      icon: <TestnetFlaskIcon size={20} />,
      description: 'Binance Smart Chain Testnet',
      chainId: 97,
      status: 'active'
    },
    {
      key: 'arbitrum_mainnet',
      name: 'Arbitrum One',
      icon: <ArbitrumIcon size={20} />,
      description: 'Arbitrum L2 Network',
      chainId: 42161,
      status: 'active'
    },
    {
      key: 'polygon_mainnet',
      name: 'Polygon PoS',
      icon: <PolygonIcon size={20} />,
      description: 'Polygon PoS Network',
      chainId: 137,
      status: 'active'
    },
    {
      key: 'ethereum_mainnet',
      name: 'Ethereum',
      icon: <EthereumIcon size={20} />,
      description: 'Ethereum Mainnet',
      chainId: 1,
      status: 'active'
    },
    {
      key: 'ethereum_testnet',
      name: 'Ethereum Goerli',
      icon: <EthereumIcon size={20} />,
      description: 'Ethereum Goerli Testnet',
      chainId: 5,
      status: 'active'
    }
  ];

  // Handle tab change
  const handleTabChange = (tabKey) => {
    onTabChange(tabKey);
  };

  // Handle network change
  const handleNetworkChange = (networkKey) => {
    onNetworkChange(networkKey);
    setShowNetworkDropdown(false);
  };

  // Copy address to clipboard
  const copyAddress = async () => {
    if (account) {
      try {
        await navigator.clipboard.writeText(account);
        setToast({ message: t('header.copied'), type: 'success' });
      } catch (err) {
        console.error('Failed to copy address:', err);
      }
    }
  };

  return (
    <header className={`pro-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Professional Brand Section */}
        <div className="header-brand" onClick={() => onShowAppChange && onShowAppChange(false)} style={{ cursor: 'pointer' }}>
          <div className="brand-logo">
            <div className="logo-wrapper">
              <img src="/logo.png" alt="HuanChain Logo" className="huan-logo" />
            </div>
            <div className="brand-text">
              <h1 className="brand-name">
                <span className="brand-name-main">HuanChain</span>
                <span className="brand-name-sub">DEX</span>
              </h1>
              <div className="brand-tagline">{t('header.tagline')}</div>
            </div>
          </div>
        </div>

        {/* Professional Navigation */}
        <nav className="header-navigation">
          <div className="nav-wrapper">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                className={`nav-item ${item.isActive ? 'active' : ''}`}
                onClick={() => handleTabChange(item.key)}
              >
                <div className="nav-item-content">
                  <span className="nav-icon">{item.icon}</span>
                  <span className="nav-label">{item.label}</span>
                </div>
              </button>
            ))}
          </div>
        </nav>

        {/* Professional Controls */}
        <div className="header-controls">
          {/* Language Selector Dropdown */}
          <div className="control-group language-control">
            <button
              className={`language-selector-btn ${showLanguageDropdown ? 'active' : ''}`}
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              title={t('header.language')}
            >
              <span className="lang-flag">{currentLanguageObj.flag}</span>
              <span className="lang-code">{currentLanguageObj.short}</span>
              <span className="dropdown-arrow"><ChevronDownIcon size={13} /></span>
            </button>

            {showLanguageDropdown && (
              <div className="language-dropdown">
                <div className="dropdown-header">
                  <h3>{t('header.language')}</h3>
                  <button
                    className="dropdown-close"
                    onClick={() => setShowLanguageDropdown(false)}
                    aria-label="Close"
                  >
                    <CloseIcon size={14} />
                  </button>
                </div>
                <div className="language-options">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      className={`language-option ${language === l.code ? 'selected' : ''}`}
                      onClick={() => {
                        setLanguage(l.code);
                        setShowLanguageDropdown(false);
                      }}
                    >
                      <span className="option-flag">{l.flag}</span>
                      <span className="option-label">{l.label}</span>
                      {language === l.code && (
                        <span className="option-check">
                          <CheckIcon size={14} color="#10b981" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Network Selector */}
          <div className="control-group network-control">
            <button 
              className={`network-selector ${showNetworkDropdown ? 'active' : ''}`}
              onClick={() => setShowNetworkDropdown(!showNetworkDropdown)}
            >
              <div className="network-display">
                <div className="network-icon">
                  {networkOptions.find(n => n.key === selectedNetwork)?.icon}
                </div>
                <span className="network-name">
                  {networkOptions.find(n => n.key === selectedNetwork)?.name || "Select Network"}
                </span>
                <span 
                  className="status-dot" 
                  style={{ backgroundColor: networkStatus.color, color: networkStatus.color }} 
                  title={networkStatus.name}
                />
              </div>
              <div className="dropdown-arrow">
                <span className="arrow-icon"><ChevronDownIcon size={13} /></span>
              </div>
            </button>

            {/* Network Dropdown */}
            {showNetworkDropdown && (
              <div className="network-dropdown">
                <div className="dropdown-header">
                  <h3>{t('header.selectNetwork')}</h3>
                  <button 
                    className="dropdown-close"
                    onClick={() => setShowNetworkDropdown(false)}
                    aria-label="Close"
                  >
                    <CloseIcon size={14} />
                  </button>
                </div>
                <div className="network-options">
                  {networkOptions.map((network) => (
                    <button
                      key={network.key}
                      className={`network-option ${selectedNetwork === network.key ? 'selected' : ''}`}
                      onClick={() => handleNetworkChange(network.key)}
                    >
                      <div className="option-icon">{network.icon}</div>
                      <div className="option-info">
                        <span className="option-name">{network.name}</span>
                        <span className="option-description">{network.description}</span>
                        <span className="option-chain-id">Chain ID: {network.chainId}</span>
                      </div>
                      {selectedNetwork === network.key && (
                        <div className="status-indicator active"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Wallet Section */}
          <div className="control-group wallet-control">
            {account ? (
              <div className="wallet-connected">
                <button 
                  className={`account-button ${showAccountDropdown ? 'active' : ''}`}
                  onClick={() => setShowAccountDropdown(!showAccountDropdown)}
                >
                  <div className="account-avatar">
                    <WalletIcon size={15} color="#a78bfa" />
                  </div>
                  <div className="account-info">
                    <span className="account-address">{formatAddress(account)}</span>
                    <span className="account-status">{t('header.connected')}</span>
                  </div>
                  <div className="dropdown-arrow"><ChevronDownIcon size={13} /></div>
                </button>

                {/* Account Dropdown */}
                {showAccountDropdown && (
                  <div className="account-dropdown">
                    <div className="account-header">
                      <div className="account-avatar large">
                        <WalletIcon size={22} color="#a78bfa" />
                      </div>
                      <div className="account-details">
                        <span className="account-label">{t('header.walletAddress')}</span>
                        <span className="full-address">{account}</span>
                      </div>
                    </div>
                    <div className="dropdown-actions">
                      <button className="action-button copy" onClick={copyAddress}>
                        <CopyIcon size={14} color="currentColor" />
                        <span>{t('header.copyAddress')}</span>
                      </button>
                      <button className="action-button disconnect" onClick={disconnectWallet}>
                        <ErrorIcon size={14} color="currentColor" />
                        <span>{t('header.disconnect')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button 
                className="connect-wallet-button"
                onClick={connectWallet}
              >
                <WalletIcon size={16} color="#ffffff" />
                <span>{t('header.connectWallet')}</span>
              </button>
            )}
          </div>


          {/* Mobile Menu Toggle */}
          <button 
            className={`mobile-menu-toggle ${showMobileMenu ? 'active' : ''}`}
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Toggle navigation menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      {showMobileMenu && (
        <div className="mobile-menu">
          <div className="mobile-nav">
            {navigationItems.map((item) => (
              <button
                key={item.key}
                className={`mobile-nav-item ${item.isActive ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange(item.key);
                  setShowMobileMenu(false);
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                <div className="nav-text">
                  <span className="nav-label">{item.label}</span>
                </div>
              </button>
            ))}
          </div>

          <div className="mobile-controls">
            {/* Language Selector in Mobile */}
            <div className="mobile-language">
              <span className="control-label">{t('header.language')}</span>
              <div className="mobile-lang-pills">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    className={`mobile-lang-pill ${language === l.code ? 'active' : ''}`}
                    onClick={() => setLanguage(l.code)}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Network Selector in Mobile */}
            <div className="mobile-network">
              <span className="control-label">{t('header.network')}</span>
              <select 
                value={selectedNetwork}
                onChange={(e) => {
                  onNetworkChange(e.target.value);
                  setShowMobileMenu(false);
                }}
                className="mobile-select"
              >
                {networkOptions.map((network) => (
                  <option key={network.key} value={network.key}>
                    {network.name} (Chain ID: {network.chainId})
                  </option>
                ))}
              </select>
            </div>

            {/* Wallet in Mobile */}
            {account ? (
              <div className="mobile-wallet">
                <div className="wallet-info">
                  <span className="control-label">{t('header.connected')}</span>
                  <span className="wallet-address">{formatAddress(account)}</span>
                </div>
                <button 
                  className="mobile-disconnect"
                  onClick={() => {
                    disconnectWallet();
                    setShowMobileMenu(false);
                  }}
                >
                  {t('header.disconnect')}
                </button>
              </div>
            ) : (
              <button 
                className="mobile-connect"
                onClick={() => {
                  connectWallet();
                  setShowMobileMenu(false);
                }}
              >
                {t('header.connectWallet')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </header>
  );
}

export default Header;